import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NavigationService } from '../../services/navigation/navigation.service';
import { GlobalStoreService } from '../../services/global-store/global-store.service';
import { ErreurMappingService } from '../../services/errors/erreur-mapping.service';
import { FormulaireBuilder } from '../../models/formulaire/formulaire-builder.model';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { TypeChangementEnum } from '../../constantes/type-changement.enum';
import { CoordonneesUtil } from '../../util/coordonnees-util';
import { CybermetrieCoordonneesService } from '../../services/cybermetrie/cybermetrie.service';
import { CybermetrieEnum } from '../../models/cybermetrie/cybermetrie.enum';
import { AccessibiliteService } from '../../services/accessibilite.service';
import { LangueService } from 'src/app/services/langue/langue.service';
import { LangueEnum } from '../../constantes/Langue.enum';
import {
  ErreursInputFormAdresse,
  ErreursInputFormCourriel,
  ErreursInputFormTelephone,
} from 'src/app/models/erreurs/erreur-input';
import { Subscription, tap } from 'rxjs';
import { CoordonneesClient } from 'src/app/api/model/coordonnees-client.model';
import { CompteDetention } from 'src/app/api/model/compte-detention.model';
import { RequeteModifierCoordonnees } from 'src/app/api/model/requete-modifier-coordonnees.model';
import { InfosCompte } from 'src/app/api/model/infos-compte.model';
import { Telephones, TelephonesTypeTelephoneEnum } from 'src/app/api/model/telephones.model';
import { AdresseModification, AdresseModificationTypeUniteEnum } from 'src/app/api/model/adresse-modification.model';

@Component({
  template: '',
  standalone: false,
})
export abstract class SaisieComponent implements OnInit, OnDestroy {
  protected static readonly TYPE_TEL_CHAMP = new Map<string, TelephonesTypeTelephoneEnum>([
    ['principal', TelephonesTypeTelephoneEnum.PRINCIPAL],
    ['secondaire', TelephonesTypeTelephoneEnum.SECONDAIRE],
    ['employeur', TelephonesTypeTelephoneEnum.EMPLOYEUR],
  ]);
  protected static readonly CHAMP_TYPE_TEL = new Map<
    TelephonesTypeTelephoneEnum[keyof TelephonesTypeTelephoneEnum],
    string
  >([
    [TelephonesTypeTelephoneEnum.PRINCIPAL, 'principal'],
    [TelephonesTypeTelephoneEnum.SECONDAIRE, 'secondaire'],
    [TelephonesTypeTelephoneEnum.EMPLOYEUR, 'employeur'],
  ]);

  @Input() coordonneesClient: CoordonneesClient;
  @Input() compteActif?: CompteDetention;
  typeModification: TypeChangementEnum;
  controleFormGroup: FormGroup;
  erreursFormAdresse: ErreursInputFormAdresse;
  erreursFormCourriel: ErreursInputFormCourriel;
  erreursFormTelephone: ErreursInputFormTelephone;
  dataFormSubscription: Subscription;
  comptesFiltres: CompteDetention[] = [];

  protected constructor(
    public readonly erreurMapping: ErreurMappingService,
    protected readonly globalStoreService: GlobalStoreService,
    protected readonly navigationService: NavigationService,
    protected readonly cybermetrieCoordonneesService: CybermetrieCoordonneesService,
    protected readonly accessibiliteService: AccessibiliteService,
    protected readonly langueService: LangueService,
    typeModification: TypeChangementEnum
  ) {
    this.typeModification = typeModification;
    this.setControlFormGroupAndErrorForm(typeModification);
  }

  ngOnInit(): void {
    this.accessibiliteService.setFocus('h1');
    if (this.compteActif) {
      this.comptesFiltres = this.coordonneesClient.comptesDetention.filter(
        (compte) =>
          compte.infosCarte.numeroCarte !== this.compteActif.infosCarte.numeroCarte &&
          compte.infosCompte.codeRelation !== 'ADI'
      );
    }
    this.dataFormSubscription = this.globalStoreService
      .getUpdateDataForm()
      .pipe(
        tap((data: RequeteModifierCoordonnees) => {
          if (data != null) {
            this.formUpdate(data);
          } else if (
            (this.typeModification === TypeChangementEnum.COURRIEL &&
              this.coordonneesClient.comptesDetention?.length === 1 &&
              this.coordonneesClient.comptesDetention[0].infosCompte?.adresseCourriel) ||
            (this.typeModification === TypeChangementEnum.TELEPHONE && this.coordonneesClient.telephones?.length > 0)
          ) {
            this.formUpdate(null, null, this.coordonneesClient);
          } else if (
            this.typeModification === TypeChangementEnum.COURRIEL &&
            this.coordonneesClient.comptesDetention?.length > 1 &&
            this.compteActif.infosCompte?.adresseCourriel
          ) {
            this.formUpdate(null, this.compteActif, this.coordonneesClient);
          }
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.dataFormSubscription.unsubscribe();
  }

  setControlFormGroupAndErrorForm(typeModification: TypeChangementEnum) {
    switch (typeModification) {
      case TypeChangementEnum.COORDONNEES:
        this.controleFormGroup = FormulaireBuilder.canada();
        this.erreursFormAdresse = this.erreurMapping.getFormErrorAdresseCanada(this.controleFormGroup);

        break;
      case TypeChangementEnum.COURRIEL:
        this.controleFormGroup = FormulaireBuilder.courriel();
        this.erreursFormCourriel = this.erreurMapping.getFormErrorCourriel(this.controleFormGroup);
        break;
      case TypeChangementEnum.TELEPHONE:
        this.controleFormGroup = FormulaireBuilder.telephone();
        this.erreursFormTelephone = this.erreurMapping.getFormErrorTelephone(this.controleFormGroup);
        break;
      default:
        break;
    }
  }

  formUpdate(data?: RequeteModifierCoordonnees, compteActif?: CompteDetention, coordonneesClient?: CoordonneesClient) {
    if (TypeChangementEnum.COORDONNEES === this.typeModification) {
      return this.formUpdateAddress(data?.adresseModification);
    }

    if (TypeChangementEnum.COURRIEL === this.typeModification) {
      return this.formUpdateEmail(data, compteActif, coordonneesClient);
    }

    if (TypeChangementEnum.TELEPHONE === this.typeModification) {
      const telephones = data?.telephones ?? coordonneesClient.telephones;
      return this.formUpdatePhone(telephones);
    }
  }

  private formUpdateAddress(adresseModification: AdresseModification): void {
    this.controleFormGroup?.get('rueCanada')?.setValue(adresseModification?.numeroCiviqueNomRue || null);
    this.controleFormGroup?.get('appartementCanada')?.setValue(adresseModification?.numeroUnite || null);
    this.controleFormGroup?.get('villeCanada')?.setValue(adresseModification?.nomMunicipalite || null);
    this.controleFormGroup?.get('provinceCanada')?.setValue(adresseModification?.codeSubdivisionPays || null);
    this.controleFormGroup?.get('codePostalCanada')?.setValue(adresseModification?.codePostal || null);
    this.erreursFormAdresse = this.erreurMapping.getFormErrorAdresseCanada(this.controleFormGroup);
    this.globalStoreService.saveErrorsForm(this.erreurMapping.getErrorAdresseFromErrorForm(this.erreursFormAdresse));
  }

  private formUpdateEmail(
    data?: RequeteModifierCoordonnees,
    compteActif?: CompteDetention,
    coordonneesClient?: CoordonneesClient
  ) {
    let infoCompte = data?.infosComptes?.[0];
    if (infoCompte == null) {
      infoCompte = compteActif?.infosCompte;
    }
    if (infoCompte == null) {
      infoCompte = coordonneesClient?.comptesDetention?.[0]?.infosCompte;
    }

    this.controleFormGroup?.get('courriel')?.setValue(infoCompte?.adresseCourriel || null);
    if (data?.infosComptes) {
      data.infosComptes
        .filter((item) => item.numeroCompte !== infoCompte.numeroCompte)
        .forEach((infoCompte) => {
          const cartes: FormArray = this.controleFormGroup?.get('cartes') as FormArray;
          cartes.push(new FormControl(infoCompte.numeroCompte));
        });
    }
    this.erreursFormCourriel = this.erreurMapping.getFormErrorCourriel(this.controleFormGroup);
    this.globalStoreService.saveErrorsForm(this.erreurMapping.getErrorCourrielFromErrorForm(this.erreursFormCourriel));
  }

  private formUpdatePhone(telephones: Telephones[]) {
    this.controleFormGroup
      ?.get('principal')
      ?.setValue(this.getNumeroTelephoneByType(telephones, TelephonesTypeTelephoneEnum.PRINCIPAL));
    this.controleFormGroup
      ?.get('secondaire')
      ?.setValue(this.getNumeroTelephoneByType(telephones, TelephonesTypeTelephoneEnum.SECONDAIRE));
    this.controleFormGroup
      ?.get('employeur')
      ?.setValue(this.getNumeroTelephoneByType(telephones, TelephonesTypeTelephoneEnum.EMPLOYEUR));
    this.controleFormGroup
      ?.get('numeroPoste')
      ?.setValue(this.getNumeroPosteByType(telephones, TelephonesTypeTelephoneEnum.EMPLOYEUR));
    this.erreursFormTelephone = this.erreurMapping.getFormErrorTelephone(this.controleFormGroup);
    this.globalStoreService.saveErrorsForm(
      this.erreurMapping.getErrorTelephoneFromErrorForm(this.erreursFormTelephone)
    );
  }

  cocher(event: Event) {
    const cartes: FormArray = this.controleFormGroup.get('cartes') as FormArray;
    const target = event.target as HTMLInputElement;
    if (target.checked) {
      cartes.push(new FormControl(target.value));
    } else {
      const index = cartes.value.findIndex((carte: string) => carte === target.value);
      if (index !== -1) {
        cartes.removeAt(index);
      }
    }
  }

  isChecked(numeroCompte: string): boolean {
    const cartes: FormArray = this.controleFormGroup?.get('cartes') as FormArray;
    return !!cartes.controls.find((carte) => carte.value === numeroCompte);
  }

  onSubmit() {
    if (this.controleFormGroup.valid) {
      this.globalStoreService.saveUpdateDataForm(this.construireRequeteAPartirDeLaForm());
      this.erreurMapping.resetFormsError();
      this.navigation();
    } else {
      this.formInvalid();
    }
  }

  getNumeroTelephoneByType(data: Telephones[], type: TelephonesTypeTelephoneEnum): string | null {
    if (data == null) {
      return null;
    }
    return data.find((tel) => tel.typeTelephone === type)?.numeroTelephone || null;
  }

  getNumeroPosteByType(data: Telephones[], type: TelephonesTypeTelephoneEnum): string | null {
    if (data == null) {
      return null;
    }
    return data.find((tel) => tel.typeTelephone === type)?.numeroPoste || null;
  }

  private construireRequeteAPartirDeLaForm(): RequeteModifierCoordonnees {
    const objRetour = {} as RequeteModifierCoordonnees;
    for (const control in this.controleFormGroup.controls) {
      if (control !== 'cartes') {
        this.controleFormGroup.controls[control.toString()].setValue(
          CoordonneesUtil.removeDoubleSpace(this.controleFormGroup?.controls[control]?.value?.trim())
        );
      }
    }
    switch (this.typeModification) {
      case TypeChangementEnum.COORDONNEES:
        objRetour.adresseModification = {
          codePostal: CoordonneesUtil.supprimerEspace(
            this.controleFormGroup.controls['codePostalCanada'].value
          ).toUpperCase(),
          numeroUnite: this.controleFormGroup.controls['appartementCanada'].value,
          typeUnite: this.getTypeUnite(),
          nomMunicipalite: this.controleFormGroup.controls['villeCanada'].value,
          numeroCiviqueNomRue: this.controleFormGroup.controls['rueCanada'].value,
          codeSubdivisionPays: this.controleFormGroup.controls['provinceCanada'].value,
        };
        break;
      case TypeChangementEnum.COURRIEL:
        objRetour.infosComptes = this.requeteCourriel();
        break;
      case TypeChangementEnum.TELEPHONE:
        objRetour.telephones = this.requeteTelephone();
        break;
      default:
        return undefined;
    }
    return objRetour;
  }

  private getTypeUnite(): AdresseModificationTypeUniteEnum | null {
    const numeroUnite = this.controleFormGroup.controls['appartementCanada']?.value;
    if (!numeroUnite) {
      return null;
    }
    return this.langueService.getCurrentLanguage() === LangueEnum.EN
      ? AdresseModificationTypeUniteEnum.APT
      : AdresseModificationTypeUniteEnum.APP;
  }

  private requeteCourriel(): Array<InfosCompte> {
    let infosComptes;
    if (this.compteActif) {
      infosComptes = [
        {
          adresseCourriel: this.controleFormGroup.controls['courriel'].value,
          numeroCompte: this.compteActif.infosCompte.numeroCompte,
        },
      ];
      this.controleFormGroup.controls['cartes'].value.forEach((numeroCompte: string) => {
        infosComptes.push({
          adresseCourriel: this.controleFormGroup.controls['courriel'].value,
          numeroCompte: numeroCompte,
        });
      });
    } else {
      infosComptes = [
        {
          adresseCourriel: this.controleFormGroup.controls['courriel'].value,
          numeroCompte: this.coordonneesClient.comptesDetention?.[0].infosCompte?.numeroCompte,
        },
      ];
    }
    return infosComptes;
  }

  requeteTelephone(): Telephones[] {
    return [...SaisieComponent.TYPE_TEL_CHAMP.entries()]
      .filter(([key]) => this.controleFormGroup.controls[key]?.value != null)
      .map(([key, val]) => {
        const tel = this.controleFormGroup.controls[key]?.value;
        const poste = this.controleFormGroup.controls['numeroPoste'].value;
        return SaisieComponent.creerTelephone(tel, val, poste);
      });
  }

  private static creerTelephone(
    numero: string,
    typeTel: TelephonesTypeTelephoneEnum,
    poste?: string | null
  ): Telephones {
    if (poste == null || TelephonesTypeTelephoneEnum.EMPLOYEUR !== typeTel) {
      return { numeroTelephone: numero, typeTelephone: typeTel };
    }

    return { numeroTelephone: numero, typeTelephone: typeTel, numeroPoste: poste };
  }

  private navigation() {
    switch (this.typeModification) {
      case TypeChangementEnum.COORDONNEES:
        this.navigationService.naviguerVersValiderCredit();
        break;
      case TypeChangementEnum.COURRIEL:
        this.navigationService.naviguerVersValiderCourrielCredit(
          this.compteActif,
          this.coordonneesClient?.comptesDetention
        );
        break;
      case TypeChangementEnum.TELEPHONE:
        this.navigationService.naviguerVersValiderTelephoneCredit(this.coordonneesClient?.telephones);
        break;
      default:
        this.navigationService.naviguerVersCredit();
        break;
    }
  }

  private formInvalid() {
    const erreurs = new Map<string, string>();
    let texteErreurs: string[];
    switch (this.typeModification) {
      case TypeChangementEnum.COORDONNEES:
        this.erreursFormAdresse = this.erreurMapping.getFormErrorAdresseCanada(this.controleFormGroup);
        this.globalStoreService.saveErrorsForm(
          this.erreurMapping.getErrorAdresseFromErrorForm(this.erreursFormAdresse)
        );
        texteErreurs = this.erreurMapping.getErrorAdresseFromErrorForm(this.erreursFormAdresse).map((e) => e.text);
        break;
      case TypeChangementEnum.COURRIEL:
        this.erreursFormCourriel = this.erreurMapping.getFormErrorCourriel(this.controleFormGroup);
        this.globalStoreService.saveErrorsForm(
          this.erreurMapping.getErrorCourrielFromErrorForm(this.erreursFormCourriel)
        );
        texteErreurs = this.erreurMapping.getErrorCourrielFromErrorForm(this.erreursFormCourriel).map((e) => e.text);
        break;
      case TypeChangementEnum.TELEPHONE:
        this.erreursFormTelephone = this.erreurMapping.getFormErrorTelephone(this.controleFormGroup);
        this.globalStoreService.saveErrorsForm(
          this.erreurMapping.getErrorTelephoneFromErrorForm(this.erreursFormTelephone)
        );
        texteErreurs = this.erreurMapping.getErrorTelephoneFromErrorForm(this.erreursFormTelephone).map((e) => e.text);
        break;
      default:
        break;
    }
    erreurs.set(CybermetrieEnum.MESSAGE_ERREUR, texteErreurs.join('|'));
    this.cybermetrieCoordonneesService.envoyerEvenementPersonnalise(
      CybermetrieEnum.CATEGORIE_TRANSACTION,
      CybermetrieEnum.ACTION_ERREUR_UTILISATEUR,
      CybermetrieEnum.LABEL_MODIFIER_INFOS,
      0,
      this.typeModification,
      erreurs
    );
  }

  annulerModification() {
    this.globalStoreService.saveEtatAfficherModaleAnnuler(true);
  }
}
